import { Controller } from "@hotwired/stimulus";
import { i18n } from "../lib/i18n";

/**
 * Identifier Scanner Controller
 *
 * Inline barcode scanner for identifier forms (no modal)
 * Uses html5-qrcode library to scan QR codes and barcodes from the device camera.
 */
export default class extends Controller {
  static targets = ["scannerRegion", "scanButton", "cancelButton", "error", "success", "input"];

  connect() {
    this.scanner = null;
    this.isScanning = false;
  }

  disconnect() {
    this.stopScanning();
  }

  async startScanning() {
    if (this.isScanning) {
      console.log("Scanner already running");
      return;
    }

    console.log("Starting barcode scanner...");

    try {
      // Dynamically import html5-qrcode to avoid loading it on every page
      const { Html5Qrcode } = await import("html5-qrcode");

      // Show scanner region and hide scan button
      this.scannerRegionTarget.classList.remove("hidden");
      this.scanButtonTarget.classList.add("hidden");
      this.cancelButtonTarget.classList.remove("hidden");
      this.hideError();

      // Get available cameras first to check permissions
      console.log("Requesting camera access...");
      const devices = await Html5Qrcode.getCameras();
      console.log("Available cameras:", devices);

      if (!devices || devices.length === 0) {
        throw new Error(i18n.t("identifier_scanner.no_cameras"));
      }

      // Initialize scanner
      console.log("Initializing scanner...");
      this.scanner = new Html5Qrcode("identifier-scanner-region");
      this.isScanning = true;

      // Start scanning with back camera preference
      console.log("Starting camera with environment facing mode...");

      // Configure for PDF417 barcodes (driver's licenses)
      // Using a wider, shorter scanning box optimized for PDF417 format
      const config = {
        fps: 10,
        qrbox: { width: 350, height: 150 },  // Wider box for PDF417 barcodes
        aspectRatio: 1.0,
        // Disable default image-based scanning, use only video stream
        disableFlip: false,
        // Try without formatsToSupport to use library defaults
        // The library should auto-detect all supported formats
      };

      console.log("Scanner config:", config);
      console.log("Note: Using default format detection (includes PDF417, QR, and all common barcodes)");

      await this.scanner.start(
        { facingMode: "environment" },
        config,
        this.onScanSuccess.bind(this),
        this.onScanFailure.bind(this)
      );
      console.log("Scanner started successfully with all barcode formats enabled");
    } catch (err) {
      console.error("Failed to start scanner:", err);

      let errorMessage = i18n.t("identifier_scanner.camera_error");

      if (err.name === "NotAllowedError" || err.message.includes("Permission")) {
        errorMessage += i18n.t("identifier_scanner.camera_permissions_denied");
      } else if (err.message.includes(i18n.t("identifier_scanner.no_cameras"))) {
        errorMessage += i18n.t("identifier_scanner.camera_no_cameras");
      } else {
        errorMessage += i18n.t("identifier_scanner.camera_permissions");
      }

      this.showError(errorMessage);
      this.resetUI();
    }
  }

  onScanSuccess(decodedText, decodedResult) {
    console.log("Barcode detected!", decodedText);
    console.log("Decoded result:", decodedResult);

    // Stop scanning immediately to prevent multiple reads
    this.scanner.stop().then(() => {
      // Null out scanner so stopScanning() doesn't try to stop it again
      this.scanner = null;

      // Populate input field
      if (this.hasInputTarget) {
        console.log("Setting input value to:", decodedText);
        this.inputTarget.value = decodedText;
        // Trigger input event for any listeners
        this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
        console.log("Input field updated");

        // Show success message
        this.showSuccess(i18n.t("identifier_scanner.scanned", { value: decodedText }));
      } else {
        console.error("No input target found!");
      }

      this.stopScanning();
    }).catch(err => {
      console.error("Error stopping scanner:", err);
      this.stopScanning();
    });
  }

  onScanFailure(error) {
    // QR code not detected - this is normal, just continue scanning
    // Only log occasionally to avoid console spam, but log actual errors
    if (error && !error.includes("NotFoundException")) {
      console.log("Scan failure (not a 'not found' error):", error);
    } else if (Math.random() < 0.01) {
      console.log("Scanning... (no barcode detected yet)");
    }
  }

  stopScanning() {
    console.log("Stopping scanner...");
    this.isScanning = false;

    if (this.scanner) {
      this.scanner
        .stop()
        .then(() => {
          console.log("Scanner stopped");
        })
        .catch(() => {
          console.log("Scanner was already stopped");
        })
        .finally(() => {
          this.scanner = null;
          this.resetUI();
        });
    } else {
      this.resetUI();
    }
  }

  resetUI() {
    this.scannerRegionTarget.classList.add("hidden");
    this.scanButtonTarget.classList.remove("hidden");
    this.cancelButtonTarget.classList.add("hidden");
  }

  showError(message) {
    console.log("Showing error:", message);
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }
    this.hideSuccess();
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
      this.errorTarget.textContent = "";
    }
  }

  showSuccess(message) {
    console.log("Showing success:", message);
    if (this.hasSuccessTarget) {
      this.successTarget.textContent = message;
      this.successTarget.classList.remove("hidden");
    }
    this.hideError();
  }

  hideSuccess() {
    if (this.hasSuccessTarget) {
      this.successTarget.classList.add("hidden");
      this.successTarget.textContent = "";
    }
  }
}
